/**
 * @file: useVoiceRecording.web.ts
 * @copyright: (c) 2019-2026 sichuan zhichetech co., ltd.
 *
 * Web voice recorder — same API as the native `useVoiceRecording.ts`.
 * Records via expo-av (MediaRecorder → webm; the media service transcodes
 * it to mp3 on upload). expo-av reports no metering on web, so we tap the
 * mic with a Web Audio AnalyserNode and compute the 0..1 level ourselves —
 * this drives both the live recording bar and the persisted waveform.
 */

import { Audio } from 'expo-av';
import { useCallback, useEffect, useRef } from 'react';

// Match the native cadence so the waveform looks the same on every client.
const METER_INTERVAL_MS = 90;
// Speech RMS is small (~0.05–0.2); scale it into the 0..1 bar range.
const WEB_METER_GAIN = 4;

// `format`/`sampleRate` only matter for native ASR (dictation), which is
// disabled on web — the values here are placeholders to satisfy the shared
// shape. Voice MESSAGES carry their own mimeType from the composer.
const SAMPLE_RATE = 16000;
const FORMAT: 'wav' | 'aac' = 'aac';

const RECORDING_OPTIONS: Audio.RecordingOptions = {
  ...Audio.RecordingOptionsPresets.HIGH_QUALITY,
  isMeteringEnabled: true,
  web: {
    mimeType: 'audio/webm',
    bitsPerSecond: 128000,
  },
};

export type VoiceRecordingResult = {
  uri: string;
  format: 'wav' | 'aac';
  sampleRate: 16000;
};

export type VoiceRecording = {
  start: () => Promise<void>;
  stop: () => Promise<VoiceRecordingResult>;
  cancel: () => Promise<void>;
};

export const useVoiceRecording = (opts?: {
  /** Receives a normalized 0..1 input level on each metering tick. */
  onMeter?: (level: number) => void;
}): VoiceRecording => {
  const recordingRef = useRef<Audio.Recording | null>(null);
  const onMeterRef = useRef(opts?.onMeter);
  onMeterRef.current = opts?.onMeter;
  // AudioContext + analyser + polling timer + the analyser's own mic stream
  // (separate from expo-av's recording stream).
  const meterRef = useRef<{
    ctx: AudioContext;
    stream: MediaStream;
    timer: ReturnType<typeof setInterval>;
  } | null>(null);

  const stopMetering = useCallback(() => {
    const m = meterRef.current;
    meterRef.current = null;
    if (!m) return;
    clearInterval(m.timer);
    m.stream.getTracks().forEach(t => t.stop());
    void m.ctx.close().catch(() => undefined);
  }, []);

  const startMetering = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const Ctx =
        window.AudioContext ||
        (window as unknown as { webkitAudioContext: typeof AudioContext })
          .webkitAudioContext;
      const ctx = new Ctx();
      await ctx.resume().catch(() => undefined);
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      const buf = new Uint8Array(analyser.fftSize);
      const timer = setInterval(() => {
        analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (const sample of buf) {
          const v = (sample - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buf.length);
        onMeterRef.current?.(Math.min(1, rms * WEB_METER_GAIN));
      }, METER_INTERVAL_MS);
      meterRef.current = { ctx, stream, timer };
    } catch (e) {
      // Metering is cosmetic — a failure here must not block recording.
      console.warn('web metering unavailable: %s', e);
    }
  }, []);

  const start = useCallback(async () => {
    if (recordingRef.current) {
      // Defensive: dispose any leftover recording from a previous failure.
      try {
        await recordingRef.current.stopAndUnloadAsync();
      } catch {
        /* ignore */
      }
      recordingRef.current = null;
    }
    const perm = await Audio.requestPermissionsAsync();
    if (perm.status !== 'granted') {
      throw new Error('microphone permission denied');
    }
    const { recording } = await Audio.Recording.createAsync(RECORDING_OPTIONS);
    recordingRef.current = recording;
    await startMetering();
  }, [startMetering]);

  const stop = useCallback(async (): Promise<VoiceRecordingResult> => {
    const r = recordingRef.current;
    if (!r) throw new Error('no active recording');
    recordingRef.current = null;
    stopMetering();
    await r.stopAndUnloadAsync();
    const uri = r.getURI();
    if (!uri) throw new Error('recording produced no uri');
    return { uri, format: FORMAT, sampleRate: SAMPLE_RATE };
  }, [stopMetering]);

  const cancel = useCallback(async () => {
    const r = recordingRef.current;
    stopMetering();
    if (!r) return;
    recordingRef.current = null;
    try {
      await r.stopAndUnloadAsync();
    } catch (e) {
      console.warn('cancel: stopAndUnloadAsync failed: %s', e);
    }
  }, [stopMetering]);

  useEffect(() => {
    return () => {
      stopMetering();
      if (recordingRef.current) {
        const r = recordingRef.current;
        recordingRef.current = null;
        void r.stopAndUnloadAsync().catch(() => undefined);
      }
    };
  }, [stopMetering]);

  return { start, stop, cancel };
};
